<template>
  <Teleport to="body">
    <Transition name="action-feedback">
      <div v-if="show" class="action-feedback-overlay">
        <v-card
          :theme="uiStore.effectiveTheme"
          class="action-feedback-content"
          :class="`action-feedback-content--${type}`"
          :color="uiStore.isDarkTheme ? 'surface-variant' : 'surface'"
          elevation="10"
          rounded="xl"
        >
          <v-icon :icon="icon" class="action-feedback-icon" />
          <span class="action-feedback-text">{{ text }}</span>
        </v-card>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useUiStore } from '@/stores/ui';

defineProps<{
  show: boolean;
  icon: string;
  text: string;
  type?: 'success' | 'info' | 'error';
}>();

const uiStore = useUiStore();
</script>

<style lang="scss">
// Action Feedback Overlay Animation (unscoped for Teleport)
.action-feedback-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  pointer-events: none;
}

.action-feedback-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 28px;
  // Background/shadow handled by v-card
  
  &--success {
    .action-feedback-icon {
      color: rgb(var(--v-theme-success));
    }
  }

  &--info {
    .action-feedback-icon {
      color: rgba(var(--v-theme-on-surface), 0.5);
    }
  }

  &--error {
    .action-feedback-icon {
      color: rgb(var(--v-theme-error));
    }
  }
}

.action-feedback-icon {
  font-size: 36px !important;
  animation: feedback-icon-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.action-feedback-text {
  font-size: 14px;
  font-weight: 600;
  // Color handled by v-card
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

// Transition animations
.action-feedback-enter-active {
  animation: feedback-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.action-feedback-leave-active {
  animation: feedback-out 0.2s ease-in forwards;
}

@keyframes feedback-in {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@keyframes feedback-out {
  0% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
}

@keyframes feedback-icon-pop {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
